import React from 'react';
import { Box, Divider, Link, Type } from '@nerdwallet/currency';
import classNames from 'classnames';
import { format } from 'date-fns';
import styles from './dynamic-press-releases-archive.module.scss';
import usePressReleasesArchive, {
  PressRelease,
} from './use-press-releases-archive';

type ArchiveNewsItemProps = {
  title: string;
  date: string;
  url: string;
  mediaSites: { name: string }[];
};

const ArchiveNewsItem = ({
  title,
  date,
  url,
  mediaSites = [],
}: ArchiveNewsItemProps) => {
  return (
    <Box
      marginBottom="4"
      paddingLeft="2"
      paddingRight="3"
      className={styles.pressBrowserItem}
    >
      <Box paddingBottom="2">
        <Type bold>
          <Link
            aria-label={
              mediaSites?.[0]?.name +
              title +
              format(new Date(date), 'MMMM d, yyyy')
            }
            href={encodeURI(url || '')} // Use encodeURI to sanitize the URL before passing to href
          >
            {mediaSites?.[0]?.name}
          </Link>
          {mediaSites?.[0]?.name ? ', ' : ''}{' '}
          {format(new Date(date), 'MMMM d, yyyy')}
        </Type>
      </Box>
      <Type>{title}</Type>
    </Box>
  );
};

type DynamicPressReleasesArchiveProps = {
  offset?: number;
  limit?: number;
};

export const DynamicPressReleasesArchive = ({
  offset = 0,
  limit = 10,
}: DynamicPressReleasesArchiveProps) => {
  const {
    articles,
    current,
    hasMore,
    handleSetNextSlide,
    handlePrevSlide,
    isLoading,
    error,
  } = usePressReleasesArchive(offset, limit);

  if (error) {
    return null;
  }

  return (
    <Box marginY={{ desktop: '5', mobile: '4' }}>
      <Box
        id="_archive-browser"
        tabIndex={0}
        aria-label="Press Archive Browser"
        className={styles.pressBrowserContainer}
        marginX={{ desktop: '4' }}
        paddingX={{ desktop: '5', mobile: '3' }}
      >
        <Box>
          <Box className={styles.pressBrowserCarousel}>
            <Box
              className={classNames(styles.pressBrowserCarouselWrapper, {
                [styles.pressBrowserCarouselLoading]: isLoading,
              })}
              justifyContent="center"
              display="flex"
              flexWrap="wrap"
            >
              {articles.map((item: PressRelease) => (
                <ArchiveNewsItem key={item.title} {...item} />
              ))}
            </Box>
          </Box>
          <Divider />
          <Box display="flex" justifyContent="space-between" marginTop="4">
            {current > 0 ? (
              <Link bold disabled={isLoading} onClick={handlePrevSlide}>
                &larr; PREVIOUS
              </Link>
            ) : (
              <div />
            )}
            {hasMore ? (
              <Link bold disabled={isLoading} onClick={handleSetNextSlide}>
                MORE STORIES &rarr;
              </Link>
            ) : (
              <div />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
